import { MagnifyingGlassIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { companyKpi } from "@/app/paths";
import { entityQueryOptions } from "@/pages/company-kpi/_shared/entity-query-options";
import { Company } from "@/types/proto/exabel/kpi/adc_messages";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/ui/combobox";
import { Flag } from "@/ui/flag";
import { InputGroupAddon, InputGroupText } from "@/ui/input-group";

import { useTrackCompanySearched } from "./events";
import { useAdcCompanySearch } from "./use-adc-company-search";

export const AdcCompanySearch = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const anchorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const queryClient = useQueryClient();
  const { data, isLoading } = useAdcCompanySearch(debouncedSearchTerm);
  const companies = data?.companies || [];

  const isOpen = searchTerm.length > 0;

  useTrackCompanySearched(debouncedSearchTerm);

  const handleSelect = (company: Company) => {
    navigate(companyKpi(company.name || ""), {
      state: { company, fromLanding: pathname === "/" },
    });
    setSearchTerm("");
  };

  return (
    <Combobox
      value={null}
      onValueChange={(value: Company | null) => value && handleSelect(value)}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) setSearchTerm("");
      }}
      filteredItems={companies}
    >
      <div ref={anchorRef} className="w-full max-w-[800px]">
        <ComboboxInput
          placeholder="Search companies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          showTrigger={false}
          showClear={false}
          className="w-full bg-white"
        >
          <InputGroupAddon align="inline-start" className="">
            <InputGroupText>
              <MagnifyingGlassIcon />
            </InputGroupText>
          </InputGroupAddon>
        </ComboboxInput>
      </div>

      <ComboboxContent anchor={anchorRef} className="min-w-(--anchor-width)">
        <ComboboxList>
          {isLoading ? (
            <div className="flex justify-center py-2">
              <SpinnerGapIcon className="size-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            companies.map((company) => (
              <ComboboxItem
                key={company.name}
                value={company}
                disabled={!company.hasAdc}
                className="gap-3"
                onMouseEnter={() =>
                  queryClient.prefetchQuery(entityQueryOptions(company.name))
                }
              >
                <Flag code={company.country?.isoAlpha2} />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate font-medium">
                    {company.displayName || "–"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {company.ticker}
                  </span>
                </div>
              </ComboboxItem>
            ))
          )}
          <ComboboxEmpty>No results</ComboboxEmpty>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
